// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
// Proprietary and confidential. See LICENSE file in project root.
//
// Opt-in toggle for the weekly portfolio digest — a regular Monday email listing
// every holding + watchlist grade and its week-over-week change. Available to all
// tiers; defaults OFF. Self-contained (inline styles) so it can drop anywhere.
import React, { useState, useEffect } from 'react';
import api from '../services/api';

const card = {
  background: 'var(--bg-elevated, #222)',
  border: '1px solid var(--border-medium, #484848)',
  borderRadius: 10,
  padding: '14px 16px',
  margin: '0 0 16px',
};

const toggleBtn = (on) => ({
  position: 'relative',
  width: 46,
  height: 26,
  flex: '0 0 auto',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  background: on ? '#10B981' : '#484848',
  transition: 'background 0.2s ease',
});

const knob = (on) => ({
  position: 'absolute',
  top: 3,
  left: on ? 23 : 3,
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: '#fff',
  transition: 'left 0.2s ease',
});

export default function WeeklyDigestToggle() {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/alerts/digest-prefs');
        if (!cancelled) setEnabled(!!data.weekly_digest_enabled);
      } catch {
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    if (enabled === null || saving) return;
    setSaving(true);
    setMsg(null);
    const next = !enabled;
    try {
      const { data } = await api.put('/alerts/digest-prefs', { weekly_digest_enabled: next });
      setEnabled(!!data.weekly_digest_enabled);
      setMsg(next
        ? "You're subscribed — the next email lands Monday morning."
        : 'Unsubscribed from the weekly grade email.');
    } catch {
      setMsg('Could not update that setting. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (enabled === null) return null;  // still loading / not logged in

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary, #F5F5F5)', fontSize: '0.95rem' }}>
            📩 Weekly grade email
          </div>
          <div style={{ color: 'var(--text-secondary, #B8B8B8)', fontSize: '0.8rem', marginTop: 3, lineHeight: 1.4 }}>
            A Monday email with every holding &amp; watchlist grade and its week-over-week change.
          </div>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          aria-pressed={enabled}
          aria-label="Toggle weekly grade email"
          style={toggleBtn(enabled)}
        >
          <span style={knob(enabled)} />
        </button>
      </div>
      {msg && (
        <div style={{ color: enabled ? '#10B981' : '#B8B8B8', fontSize: '0.78rem', marginTop: 10 }}>
          {msg}
        </div>
      )}
    </div>
  );
}
